"use client";

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { Home, AlertCircle } from 'lucide-react';
import { RiCustomerServiceFill } from "react-icons/ri";

// Dynamically import Lottie to avoid SSR issues
const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

export default function Error({ error, reset }) {
  const router = useRouter();
  const [animationData, setAnimationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animationError, setAnimationError] = useState(false);

  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Application error:', error);

    // Auto-reload on ChunkLoadError
    if (error && (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk') || error.message?.includes('ChunkLoadError'))) {
      const lastReload = sessionStorage.getItem('chunk-error-reload');
      const now = Date.now();
      
      // If we haven't reloaded due to a chunk error in the last 15 seconds, reload the page
      if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
        sessionStorage.setItem('chunk-error-reload', String(now));
        console.log('ChunkLoadError detected. Performing automatic reload to fetch latest assets...');
        window.location.reload();
        return;
      }
    }

    try {
      fetch('/api/log-error?msg=' + encodeURIComponent(error?.message || 'Unknown error'));
    } catch (e) {
      console.error('Failed to log error:', e);
    }
  }, [error]);

  useEffect(() => {
    // Mark this page as an error page to hide chatbot
    document.body.setAttribute('data-page-type', 'error');
    
    // Load the internal-error.json animation file with cache busting
    const animationUrl = `/internal-error.json?t=${Date.now()}`;
    console.log('Loading error animation from:', animationUrl);
    
    fetch(animationUrl, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
      }
    })
      .then((res) => {
        console.log('Animation fetch response:', res.status, res.url);
        if (!res.ok) {
          throw new Error('Failed to load animation');
        }
        return res.json();
      })
      .then((data) => {
        // Verify we got valid animation data
        console.log('Animation data loaded, keys:', Object.keys(data || {}));
        if (data && (data.v || data.fr || data.op)) {
          setAnimationData(data);
          setLoading(false);
        } else {
          throw new Error('Invalid animation data');
        }
      })
      .catch((err) => {
        console.error('Error loading internal error animation:', err);
        setAnimationError(true);
        setLoading(false);
      });

    // Cleanup: remove the attribute when component unmounts
    return () => {
      document.body.removeAttribute('data-page-type');
    };
  }, []);

  return (
    <div style={{ height: '98vh', width: '99vw', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(16px, 4vw, 24px)', margin: '0', overflow: 'hidden' }}>
      <div className="error-container" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '1200px', flexWrap: 'wrap' }}>
        {/* Animation on the left */}
        <div className="animation-section" style={{ flex: '1', minWidth: '400px', maxWidth: '900px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="animation-container" style={{ width: '100%', maxWidth: '700px', height: 'auto', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0', padding: '0', lineHeight: '0' }}>
            {loading ? (
              <div style={{ width: '48px', height: '48px', border: '3px solid #e5e7eb', borderTopColor: '#3a599c', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            ) : animationError || !animationData ? (
              <AlertCircle style={{ width: '120px', height: '120px', color: '#9ca3af' }} />
            ) : (
              <Lottie 
                animationData={animationData}
                loop={true}
                autoplay={true}
                style={{ width: '100%', height: '100%', margin: '0', padding: '0', display: 'block' }}
              />
            )}
          </div>
        </div>

        {/* Text content on the right */}
        <div className="text-section" style={{ flex: '1', minWidth: '300px', maxWidth: '600px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 'clamp(16px, 3vw, 24px)' }}>
          <h1 className="error-heading" style={{ fontSize: 'clamp(24px, 4vw, 42px)', fontWeight: '700', color: '#1f2937', margin: '0', lineHeight: '1.2' }}>
            Something went wrong.
          </h1>
          <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: '#6b7280', margin: '0', lineHeight: '1.6' }}>
          An unexpected error occurred on our server. We’re working to fix it as quickly as possible. Please try again in a moment.
          </p>
          
          <div style={{ display: 'flex', flexDirection: 'row', gap: 'clamp(12px, 2vw, 16px)', marginTop: 'clamp(8px, 2vw, 16px)', flexWrap: 'wrap' }}>
            <button
              onClick={() => router.push('/contact-us')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: 'clamp(10px, 1.5vw, 14px) clamp(20px, 3vw, 28px)',
                backgroundColor: '#3b82f5',
                color: 'white',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'clamp(12px, 1.8vw, 14px)',
                fontWeight: '600',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f5';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              }}
            >
              <RiCustomerServiceFill size={18} />
              Contact Us
            </button>
            <button
              onClick={() => router.push('/')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: 'clamp(10px, 1.5vw, 14px) clamp(20px, 3vw, 28px)',
                backgroundColor: 'white',
                color: '#4b5563',
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                cursor: 'pointer',
                fontSize: 'clamp(12px, 1.8vw, 14px)',
                fontWeight: '600',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
              }}
            >
              <Home size={18} />
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        :global(div[style*="lottie"]) {
          margin: 0 !important;
          padding: 0 !important;
        }
        @media (max-width: 768px) {
          .error-container {
            flex-direction: column !important;
            gap: clamp(24px, 5vw, 32px) !important;
          }
          .animation-section {
            max-width: 100% !important;
            min-width: auto !important;
          }
          .text-section {
            max-width: 100% !important;
            min-width: auto !important;
            text-align: center !important;
          }
          .animation-container {
            max-width: min(500px, 85vw) !important;
          }
        }
        @media (max-width: 480px) {
          .error-container {
            gap: clamp(20px, 4vw, 24px) !important;
          }
          .animation-container {
            max-width: min(400px, 90vw) !important;
          }
        }
      `}</style>
    </div>
  );
}

